import { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ACTION_TONE = { create: "default", update: "secondary", error: "destructive" };

export default function ClientImport({ onDone }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, summary, ch_configured }
  const [enrich, setEnrich] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const reset = () => { setPreview(null); setFileName(""); setBusy(false); };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/clients/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "CASS-client-import-template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx") && !f.name.toLowerCase().endsWith(".xls")) return toast.error("Please choose an Excel .xlsx file");
    setBusy(true); setFileName(f.name);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/clients/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setEnrich(!!data.ch_configured);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not read the file"); reset(); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    const valid = preview.rows.filter((r) => r.action !== "error").map((r) => r.data);
    if (!valid.length) return toast.error("Nothing to import — every row has an error");
    setBusy(true);
    try {
      const { data } = await api.post("/clients/import/commit", { rows: valid, enrich });
      toast.success(`Import complete — ${data.created} created, ${data.updated} updated`);
      if (data.errors?.length) toast.error(`${data.errors.length} row(s) failed to save`);
      setOpen(false); reset(); onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="import-clients-btn">
        <Upload className="w-4 h-4 mr-2" /> Import
      </Button>
      <DialogContent className="max-w-3xl" data-testid="client-import-dialog">
        <DialogHeader><DialogTitle>Import clients from Excel</DialogTitle></DialogHeader>

        {!preview ? (
          <div className="space-y-4">
            <p className="text-sm text-cass-slate">Download the template, fill in your client rows, then upload it. You'll see a preview before anything is saved. Existing records (matched on company number, else legal name) are updated.</p>
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="w-4 h-4 mr-2" />Download Excel template</Button>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <FileSpreadsheet className="w-8 h-8 mx-auto text-cass-slate mb-2" />
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="import-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-cass-navy text-white" data-testid="choose-file-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Choose spreadsheet
              </Button>
              {fileName && <div className="text-xs text-cass-slate mt-2">{fileName}</div>}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-sm" data-testid="import-summary">
              <Badge>{preview.summary.create} new</Badge>
              <Badge variant="secondary">{preview.summary.update} updates</Badge>
              {preview.summary.error > 0 && <Badge variant="destructive">{preview.summary.error} errors</Badge>}
              <span className="text-cass-slate">of {preview.summary.total} rows</span>
            </div>
            <div className="max-h-[45vh] overflow-y-auto border border-border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Row</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Company no.</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r) => (
                    <TableRow key={r.row} data-testid={`import-row-${r.row}`}>
                      <TableCell className="text-cass-slate">{r.row}</TableCell>
                      <TableCell className="font-medium text-cass-navy">{r.name}</TableCell>
                      <TableCell className="text-sm">{r.company_number || "—"}</TableCell>
                      <TableCell><Badge variant={ACTION_TONE[r.action]}>{r.action}</Badge></TableCell>
                      <TableCell className="text-xs text-red-600">{(r.errors || []).join("; ")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {preview.ch_configured && (
              <label className="flex items-center gap-2 text-sm text-cass-navy">
                <input type="checkbox" checked={enrich} onChange={(e) => setEnrich(e.target.checked)} data-testid="import-enrich" />
                Auto-fill missing details (year end, CS01, address, directors) from Companies House
              </label>
            )}
          </div>
        )}

        <DialogFooter>
          {preview && <Button variant="ghost" onClick={reset} data-testid="import-back-btn">Choose a different file</Button>}
          {preview && (
            <Button onClick={confirm} disabled={busy} className="bg-cass-teal text-white" data-testid="confirm-import-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Import {preview.summary.create + preview.summary.update} record{preview.summary.create + preview.summary.update === 1 ? "" : "s"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
